import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { APIService } from 'src/api/api';
import { REPORTS } from 'src/components/reports/reportService';
import { getFormattedDate } from 'src/utils/getInitials';

const initialState = {
  mileageList: [],
  customisedMileageList: [],
  reconciliationList: [],
  otherReportList: [],
  totalCount: 0,
  customisedMileageTotalCount: 0,
  nextPage: null,
  loading: false,
  customisedMileageLoading: false
};

const hasVehicleNumber = (item = {}) => {
  const candidate = item.vehicle_number
    || item.registration_num
    || item.vehicle_reg_num
    || item.vehicle_registration_number
    || item.vehicle_no;
  return candidate !== null && candidate !== undefined && String(candidate).trim() !== '';
};

const getFirstAvailableValue = (item = {}, keys = []) => {
  const foundKey = keys.find((key) => item[key] !== null && item[key] !== undefined && item[key] !== '');
  return foundKey ? item[foundKey] : '';
};

export const getMileageList = createAsyncThunk(
  'reports/mileage',
  async (req, { rejectWithValue }) => {
    const { limit, pageNo, params } = req;
    try {
      const res = await APIService.getMileageData(limit, pageNo, params);
      if (!res.success) throw res;
      return res.result;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const loadMoreMileageList = createAsyncThunk(
  'reports/mileage/loadmore',
  async (req, { rejectWithValue }) => {
    const { limit, pageNo, params } = req;
    try {
      const res = await APIService.getMileageData(limit, pageNo, params);
      if (!res.success) throw res;
      return res.result;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const getCustomisedMileageList = createAsyncThunk(
  'reports/customised-mileage',
  async (req, { rejectWithValue }) => {
    const { limit, pageNo, params } = req;
    try {
      const res = await APIService.getCustomisedMileageData(limit, pageNo, params);
      if (!res.success) throw res;
      return res.result;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const getReconciliationList = createAsyncThunk(
  'reports/reconciliation',
  async (req, { rejectWithValue }) => {
    const { limit, pageNo, params } = req;
    try {
      const res = await APIService.getReconciliationData(limit, pageNo, params);
      if (!res.success) throw res;
      return res.result;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const loadMoreReconciliationList = createAsyncThunk(
  'reports/reconciliation/loadmore',
  async (req, { rejectWithValue }) => {
    const { limit, pageNo, params } = req;
    try {
      const res = await APIService.getReconciliationData(limit, pageNo, params);
      if (!res.success) throw res;
      return res.result;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const getOtherReportsData = createAsyncThunk(
  'reports/other-report',
  async (req, { rejectWithValue }) => {
    const { url, reportName } = req;
    try {
      const res = await APIService.getOtherReportsData(url);
      if (res?.success === false) throw res;
      const data = res.result?.results || res.result;
      const count = res.result?.count || data.length;
      return {
        count,
        data,
        nextPage: res.result?.next || null,
        reportName
      };
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

function flatObject(item) {
  const flatItem = {};
  const ItemKeys = Object.keys(item);
  ItemKeys.forEach((key) => {
    const value = item[key];
    if (typeof value !== 'object') {
      let listItem = value;
      if (key.toLowerCase().includes('time') || key.toLowerCase().includes('date')) {
        listItem = getFormattedDate(value);
      }
      flatItem[key] = listItem;
    }
  });
  return flatItem;
}

const formatOtherReportData = (data) => {
  const updatedData = data.map((item) => flatObject(item));
  return updatedData;
};

const formatVehicleWiseFuelConsumptionData = (data) => data
  .filter((item) => hasVehicleNumber(item))
  .map((item, index) => ({
    si_no: index + 1,
    date: getFirstAvailableValue(item, ['date', 'start_time', 'date_time', 'filled_time', 'transaction_date']),
    contractor_name: getFirstAvailableValue(item, ['contractor_name', 'contractor', 'contractor_label']),
    po_number: getFirstAvailableValue(item, ['po_number']),
    department: getFirstAvailableValue(item, ['department', 'department_name']),
    vehicle_type: getFirstAvailableValue(item, ['vehicle_type', 'type', 'model']),
    quantity: getFirstAvailableValue(item, ['quantity', 'fuel_quantity', 'total_quantity']),
    site: getFirstAvailableValue(item, ['site', 'site_name', 'location']),
    hr_or_odometer_reading: getFirstAvailableValue(item, ['hr_or_odometer_reading', 'odometer_entry', 'odometer_reading', 'hour_meter_reading']),
    totaliser_reading: getFirstAvailableValue(item, ['totaliser_reading', 'totalizer_reading'])
  }));

const formatReport = (data) => {
  const updatedData = data.map((item) => {
    const updatedItem = {
      ...item,
      ...item.mileage_info,
      ...item.card_info
    };
    return updatedItem;
  });
  return updatedData;
};

export const reportSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    resetOtherReportData: (state) => ({
      ...state, otherReportList: [], totalCount: 0, nextPage: null
    }),
    resetCustomisedMileageData: (state) => ({
      ...state, customisedMileageList: [], customisedMileageTotalCount: 0
    }),
    setShiftTimings: (state, action) => ({
      ...state, shiftTimings: action.payload
    }),
  },
  extraReducers: (builder) => {
    // add your async reducers here
    builder
      .addCase(getMileageList.fulfilled, (state, action) => ({
        ...state,
        loading: false,
        totalCount: action.payload.count,
        mileageList: formatReport(action.payload.results)
      }))
      .addCase(getMileageList.pending, (state) => ({ ...state, loading: true }))
      .addCase(getMileageList.rejected, (state) => ({ ...state, loading: false }))
      .addCase(loadMoreMileageList.pending, (state) => ({ ...state, loading: true }))
      .addCase(loadMoreMileageList.rejected, (state) => ({ ...state, loading: false }))
      .addCase(loadMoreMileageList.fulfilled, (state, action) => ({
        ...state,
        loading: false,
        totalCount: action.payload.count,
        mileageList: formatReport(action.payload.results),
      }))
      .addCase(getCustomisedMileageList.pending, (state) => ({
        ...state, customisedMileageLoading: true
      }))
      .addCase(getCustomisedMileageList.rejected, (state) => ({
        ...state, customisedMileageLoading: false
      }))
      .addCase(getCustomisedMileageList.fulfilled, (state, action) => ({
        ...state,
        customisedMileageLoading: false,
        customisedMileageTotalCount: action.payload?.count || action.payload?.length || 0,
        customisedMileageList: action.payload?.results || action.payload || []
      }))
      .addCase(getOtherReportsData.pending, (state) => ({ ...state, loading: true }))
      .addCase(getOtherReportsData.rejected, (state) => ({ ...state, loading: false }))
      .addCase(getOtherReportsData.fulfilled, (state, action) => ({
        ...state,
        loading: false,
        totalCount: action.payload.reportName === REPORTS.vehicleWiseFuelConsumption
          ? formatVehicleWiseFuelConsumptionData(action.payload.data).length
          : action.payload.count,
        nextPage: action.payload.reportName === REPORTS.vehicleWiseFuelConsumption
          ? null
          : action.payload.nextPage,
        otherReportList: action.payload.reportName === REPORTS.vehicleWiseFuelConsumption
          ? formatVehicleWiseFuelConsumptionData(action.payload.data)
          : formatOtherReportData(action.payload.data),
      }))
      .addCase(getReconciliationList.fulfilled, (state, action) => ({
        ...state,
        loading: false,
        totalCount: action.payload.count,
        reconciliationList: action.payload.results
      }))
      .addCase(getReconciliationList.pending, (state) => ({ ...state, loading: true }))
      .addCase(getReconciliationList.rejected, (state) => ({ ...state, loading: false }))
      .addCase(loadMoreReconciliationList.pending, (state) => ({ ...state, loading: true }))
      .addCase(loadMoreReconciliationList.rejected, (state) => ({ ...state, loading: false }))
      .addCase(loadMoreReconciliationList.fulfilled, (state, action) => ({
        ...state,
        loading: false,
        totalCount: action.payload.count,
        reconciliationList: action.payload.results,
      }));
  }
});

// Action creators are generated for each case reducer function
export const {
  setShiftTimings,
  resetOtherReportData,
  resetCustomisedMileageData,
} = reportSlice.actions;

export default reportSlice.reducer;
